import React, { useEffect } from 'react'
import { useRouteError, isRouteErrorResponse } from 'react-router'
import { InfoBox } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { Analytics as AnalyticsType } from '../analytics/Analytics'
import { Links } from '@evertel/constants'

/**
 * Note that a standard error boundary must use a class component since there is
 * no functional hook for getDerivedStateFromError or componentDidCatch methods.
 * But, react-router api routes takes a "errorElement" which this is for which is
 * slightly different than a normal ErrorBoundary.
 * @returns
 */
const ErrorBoundary: React.FC = () => {
    const error = useRouteError()

    const analytics = useService(Analytics) as AnalyticsType

    useEffect(() => {
        const errorObj = {
            type: 'FATAL',
            URL: window.location.href,
            error: error instanceof Error ? error.toString() : 'Unknown error',
            errorInfo: error
        }

        console.error('Error Boundary:', errorObj)

        // log to GA
        analytics.logException(errorObj.error, true)
    }, [error])

    let errorMessage: string
    let errorDetails: string | null = null

    if (isRouteErrorResponse(error)) {
    // Error is a response from a loader or action
        errorMessage = `${error.status} ${error.statusText}`
        errorDetails = error.data?.message || JSON.stringify(error.data)
    } else if (error instanceof Error) {
        errorMessage = error.message
        errorDetails = error.stack
    } else {
        errorMessage = 'Unknown error'
        errorDetails = JSON.stringify(error)
    }

    return (
        <div className="p-5 app fadeIn animated registration" style={{height: '100vh'}}>
            <InfoBox color="warning">
                <h1>Hmm. Something went wrong.</h1>
                <h5>Our engineers have been automatically notified. Try going back or refreshing your page. If you continue to experience this issue, please email us at <a href="mailto:EvertelSupport@genasys.com">EvertelSupport@genasys.com</a> or try our <a href={Links.helpCenter} target="_blank" rel="noreferrer">Help Center</a>.
                </h5>
                <details className="pt-4" style={{ whiteSpace: 'pre-wrap' }}>
                    <summary>Error Details</summary>
                    <div>
                        {errorMessage}
                        {errorDetails && (
                            <>
                                <br />
                                {errorDetails}
                            </>
                        )}
                    </div>
                </details>
            </InfoBox>
        </div>
    )
}

export default ErrorBoundary
